import React from "react";
import { useTranslation } from "react-i18next";
import classes from "./../wizard/TaskmapWizard.module.css";
import {
  VictoryAxis,
  VictoryChart,
  VictoryHistogram,
  VictoryTooltip,
} from "victory";
import { CutoffRange } from "../taskmap.types";

export default function TaskmapHistogram({
  label,
  sortedData,
  cutoff,
  setCutoff,
}: {
  label: string;
  sortedData: number[];
  cutoff: CutoffRange;
  setCutoff: (cutoff: CutoffRange) => void;
}): React.ReactNode {
  const { t } = useTranslation();

  if (sortedData.length === 0) return null;

  const maxValue = sortedData[sortedData.length - 1];
  const bins =
    maxValue > 10
      ? 15
      : [...Array(Math.floor(maxValue) + 1).keys()].map((index) => index + 0.5);

  return (
    <div className={classes.histogram} data-testid="taskmapHistogram">
      <VictoryChart height={200} animate={false}>
        <VictoryHistogram
          style={{
            data: {
              fill: ({ datum }) => {
                const datumValue = datum.x | 0;
                return datumValue >= cutoff.min && datumValue <= cutoff.max
                  ? "var(--color-selected)"
                  : "var(--color-surface-muted)";
              },
            },
          }}
          events={[
            {
              target: "data",
              eventHandlers: {
                onClick: (_, { datum }) => {
                  const datumValue = datum.x | 0;
                  if (
                    Math.abs(datumValue - cutoff.max) >
                    Math.abs(datumValue - cutoff.min)
                  ) {
                    setCutoff({ min: datumValue, max: cutoff.max });
                  } else {
                    setCutoff({ min: cutoff.min, max: datumValue });
                  }
                },
              },
            },
          ]}
          data={sortedData.map((value) => ({ x: value }))}
          bins={bins}
          labels={({ datum }) =>
            t("tooltip.{{n}} trees", { n: datum.y | 0 }) as string
          }
          labelComponent={<VictoryTooltip />}
        />
        <VictoryAxis dependentAxis fixLabelOverlap={true} />
        <VictoryAxis
          dependentAxis={false}
          fixLabelOverlap={true}
          label={label}
        />
      </VictoryChart>
    </div>
  );
}
